import React, { FC } from "react";
import { Box, Typography } from "@mui/material";

import {
  BLENDER_ICON_SRC,
  CENTRIFUGE_ICON_SRC,
  CONVEYOR_ICON_SRC,
  CRANE_ICON_SRC,
  CRUSHER_ICON_SRC,
  DEFAULT_MODE_BORDER_COLOR,
  DEFAULT_TRAIN_ICON_SRC,
  DISSOLVER_ICON_SRC,
  ELEVATOR_ICON_SRC,
  MIXER_DUAL_ICON_SRC,
  SCREEN_ICON_SRC,
  VACUUM_FILTER_ICON_SRC,
  WIDE_TRAIN_ICON_SRCS,
} from "../train-type-icons";

const resolveVisibleFrameColor = (color?: string | null): string => {
  if (!color) {
    return DEFAULT_MODE_BORDER_COLOR;
  }

  const normalized = color.trim().toLowerCase();
  if (
    normalized === "#fff" ||
    normalized === "#ffffff" ||
    normalized === "white" ||
    normalized === "rgb(255, 255, 255)" ||
    normalized === "rgba(255, 255, 255, 1)"
  ) {
    return DEFAULT_MODE_BORDER_COLOR;
  }

  return color;
};

export interface ITrainTypeIconProps {
  alarmColor: string;
  borderColor: string;
  customTypeTitle?: string | null;
  equipmentGroupColor?: string;
  equipmentGroupName?: string;
  iconSrc: string;
}

export const TrainTypeIcon: FC<ITrainTypeIconProps> = ({
  alarmColor,
  borderColor,
  equipmentGroupColor,
  equipmentGroupName,
  iconSrc,
}) => {
  const frameColor = resolveVisibleFrameColor(borderColor);
  const isBlenderIcon = iconSrc === BLENDER_ICON_SRC;
  const isCentrifugeIcon = iconSrc === CENTRIFUGE_ICON_SRC;
  const isConveyorIcon = iconSrc === CONVEYOR_ICON_SRC;
  const isCraneIcon = iconSrc === CRANE_ICON_SRC;
  const isCrusherIcon = iconSrc === CRUSHER_ICON_SRC;
  const isDissolverIcon = iconSrc === DISSOLVER_ICON_SRC;
  const isElevatorIcon = iconSrc === ELEVATOR_ICON_SRC;
  const isMixerDualIcon = iconSrc === MIXER_DUAL_ICON_SRC;
  const isScreenIcon = iconSrc === SCREEN_ICON_SRC;
  const isVacuumFilterIcon = iconSrc === VACUUM_FILTER_ICON_SRC;
  const isWideIcon = WIDE_TRAIN_ICON_SRCS.has(iconSrc);
  const isDefaultTrainIcon = iconSrc === DEFAULT_TRAIN_ICON_SRC;

  const alarmBackgroundSx = isDissolverIcon
    ? { width: 100, height: 30, top: "50%", offsetX: 0 }
    : isElevatorIcon
      ? { width: 46, height: 100, top: "50%", offsetX: 0 }
      : isMixerDualIcon
        ? { width: 100, height: 44, top: "50%", offsetX: 0 }
        : isVacuumFilterIcon
          ? { width: 100, height: 52, top: "50%", offsetX: 0 }
          : isCentrifugeIcon
            ? { width: 88, height: 76, top: "50%", offsetX: 0 }
            : isCraneIcon
              ? { width: 98, height: 34, top: "50%", offsetX: 0 }
              : isConveyorIcon
                ? { width: 100, height: 30, top: "50%", offsetX: 0 }
                : isScreenIcon
                  ? { width: 98, height: 76, top: "50%", offsetX: 0 }
                  : isCrusherIcon
                    ? { width: 78, height: 100, top: "50%", offsetX: 0 }
                    : isBlenderIcon
                      ? { width: 100, height: 22, top: "50%", offsetX: 0 }
                      : isWideIcon
                        ? { width: 100, height: 32, top: "50%", offsetX: 0 }
                        : isDefaultTrainIcon
                          ? { width: 96, height: 68, top: "46%", offsetX: 2 }
                          : { width: 78, height: 76, top: "50%", offsetX: 0 };

  return (
    <Box
      sx={{
        position: "relative",
        width: 144,
        height: 150,
        flexShrink: 0,
        boxSizing: "border-box",
        border: `3px solid ${frameColor}`,
        borderRadius: "10px",
        overflow: "visible",
        backgroundColor: "#FFFFFF",
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: alarmBackgroundSx.top,
          left: "50%",
          width: alarmBackgroundSx.width,
          height: alarmBackgroundSx.height,
          transform: `translate(calc(-50% + ${alarmBackgroundSx.offsetX}px), -50%)`,
          backgroundColor: alarmColor,
          pointerEvents: "none",
        }}
      />

      <Box
        component="img"
        src={iconSrc}
        alt=""
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          width: 112,
          height: 112,
          transform: "translate(-50%, -50%)",
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />

      {equipmentGroupName ? (
        <Box
          sx={{
            position: "absolute",
            top: -10,
            right: -5,
            minWidth: 42,
            maxWidth: 58,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 0.5,
            borderRadius: "3px",
            backgroundColor: "#ffffff",
            border: "2px solid #CFEFFF",
            color: equipmentGroupColor || "#D32F2F",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.08)",
          }}
          title={equipmentGroupName}
        >
          <Typography component="span" sx={{ fontSize: 18, lineHeight: "22px", fontWeight: 700 }}>
            {equipmentGroupName}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};
